import React, { useEffect, useMemo, useState } from "react";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { CreatePaymentIntent } from "./PaymentApi";
import {
  buildAuthRoute,
  buildPaymentRoute,
  clearCheckoutIntent,
  getStoredCheckoutEmail,
  persistCheckoutEmail,
} from "utils/checkoutFlow";
import { APP_DASHBOARD_ROUTE, SIGN_IN_ROUTE, SIGN_UP_ROUTE } from "utils/routes";

const emailRegex = /\S+@\S+\.\S+/;

const StripeCheckoutForm = ({ plan, isAuthenticated }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canContinueAfterPayment, setCanContinueAfterPayment] = useState(false);
  const [guestEmail, setGuestEmail] = useState(() => getStoredCheckoutEmail() || "");
  const [guestEmailError, setGuestEmailError] = useState(null);

  const normalizedGuestEmail = useMemo(
    () => guestEmail.trim().toLowerCase(),
    [guestEmail]
  );

  useEffect(() => {
    if (!stripe) {
      return undefined;
    }

    const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
    if (!clientSecret) {
      return undefined;
    }

    let isMounted = true;

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!isMounted || !paymentIntent) {
        return;
      }

      if (paymentIntent.status === "succeeded") {
        if (isAuthenticated) {
          clearCheckoutIntent();
        }

        setCanContinueAfterPayment(true);
        setStatusTone("success");
        setStatusMessage(
          isAuthenticated
            ? "Pagamento confirmado. Seu acesso está sendo liberado agora."
            : "Pagamento confirmado. Agora entre ou crie sua conta com o mesmo e-mail para liberar o acesso."
        );
        setErrorMessage(null);
        return;
      }

      if (paymentIntent.status === "processing") {
        if (isAuthenticated) {
          clearCheckoutIntent();
        }

        setCanContinueAfterPayment(true);
        setStatusTone("info");
        setStatusMessage(
          isAuthenticated
            ? "Pagamento em processamento. Assim que a confirmação chegar, seu plano será atualizado."
            : "Pagamento em processamento. Você já pode entrar ou criar sua conta com o mesmo e-mail enquanto aguardamos a confirmação."
        );
        setErrorMessage(null);
        return;
      }

      if (paymentIntent.status === "requires_payment_method") {
        setCanContinueAfterPayment(false);
        setStatusTone("error");
        setStatusMessage(null);
        setErrorMessage("Não foi possível confirmar o pagamento. Revise os dados e tente novamente.");
        return;
      }

      setCanContinueAfterPayment(false);
      setStatusTone("info");
      setStatusMessage("Estamos aguardando a confirmação final do pagamento.");
      setErrorMessage(null);
    });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, stripe]);

  const validateGuestEmail = () => {
    if (isAuthenticated) {
      return true;
    }

    if (!emailRegex.test(normalizedGuestEmail)) {
      setGuestEmailError("Informe um e-mail válido para continuar com o checkout.");
      return false;
    }

    persistCheckoutEmail(normalizedGuestEmail);
    setGuestEmailError(null);
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (elements == null || !plan || !stripe) {
      return;
    }

    setErrorMessage(null);
    setStatusMessage(null);
    setCanContinueAfterPayment(false);

    if (!validateGuestEmail()) {
      return;
    }

    setIsSubmitting(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setStatusMessage(null);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await CreatePaymentIntent({
        currencyCode: plan.currencyCode,
        planId: plan.tierId,
        customerEmail: isAuthenticated ? null : normalizedGuestEmail,
      });

      const { clientSecret } = response;
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${buildPaymentRoute(plan.slug)}`,
        },
        redirect: "if_required",
      });

      if (error) {
        setCanContinueAfterPayment(false);
        setErrorMessage(error.message);
        setStatusMessage(null);
        setIsSubmitting(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        if (isAuthenticated) {
          clearCheckoutIntent();
        }

        setCanContinueAfterPayment(true);
        setStatusTone("success");
        setStatusMessage(
          isAuthenticated
            ? "Pagamento confirmado. Seu plano está sendo atualizado."
            : "Pagamento confirmado. Agora entre ou crie sua conta com o mesmo e-mail para liberar o plano."
        );
        setErrorMessage(null);
        setIsSubmitting(false);
        return;
      }

      if (paymentIntent?.status === "processing") {
        if (isAuthenticated) {
          clearCheckoutIntent();
        }

        setCanContinueAfterPayment(true);
        setStatusTone("info");
        setStatusMessage(
          isAuthenticated
            ? "Pagamento enviado com sucesso e agora está em processamento."
            : "Pagamento enviado com sucesso. Entre ou crie sua conta com o mesmo e-mail enquanto a confirmação final chega."
        );
        setErrorMessage(null);
        setIsSubmitting(false);
        return;
      }

      setCanContinueAfterPayment(false);
      setStatusTone("info");
      setStatusMessage("O pagamento foi iniciado. Se houver redirecionamento do banco, você voltará para esta tela depois.");
      setErrorMessage(null);
    } catch (error) {
      setCanContinueAfterPayment(false);
      setErrorMessage(error.message);
      setStatusMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateToPostPaymentAuth = (basePath) => {
    navigate(
      buildAuthRoute(basePath, plan?.slug, {
        afterPayment: 1,
        email: normalizedGuestEmail || getStoredCheckoutEmail(),
      })
    );
  };

  const handleNavigateToDashboard = () => {
    clearCheckoutIntent();
    navigate(APP_DASHBOARD_ROUTE);
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <CopyBlock>
        <FormTitle>Pagamento seguro</FormTitle>
        <FormDescription>
          Finalize seu plano <strong>{plan.name}</strong> com a Stripe para liberar a capacidade extra
          do seu plano e, quando aplicável, os recursos avançados de alocação. O acesso vale por{" "}
          {plan.billingLabel.toLowerCase()} e não há renovação automática neste fluxo.
        </FormDescription>
      </CopyBlock>

      {!isAuthenticated && (
        <GuestIdentityCard>
          <GuestIdentityTitle>Identifique o checkout com seu e-mail</GuestIdentityTitle>
          <GuestIdentityText>
            Use um e-mail válido para que possamos associar o pagamento à sua conta depois.
          </GuestIdentityText>
          <FieldGroup>
            <FieldLabel htmlFor="checkout-email">E-mail para liberar o plano</FieldLabel>
            <EmailInput
              id="checkout-email"
              type="email"
              autoComplete="email"
              value={guestEmail}
              onChange={(event) => {
                setGuestEmail(event.target.value);
                if (guestEmailError) {
                  setGuestEmailError(null);
                }
              }}
              placeholder="seunome@exemplo.com"
              disabled={isSubmitting || canContinueAfterPayment}
            />
            <FieldHint>
              Depois do pagamento, basta entrar ou criar sua conta com esse mesmo e-mail.
            </FieldHint>
            {guestEmailError && <ValidationText>{guestEmailError}</ValidationText>}
          </FieldGroup>
        </GuestIdentityCard>
      )}

      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      <SubmitButton type="submit" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? "Processando..." : `Pagar ${plan.formattedPrice}`}
      </SubmitButton>

      <HelpText>
        Ao confirmar, seu pagamento é processado pela Stripe e a liberação do plano acontece logo
        após a confirmação.
      </HelpText>

      {statusMessage && <StatusCard $tone={statusTone}>{statusMessage}</StatusCard>}
      {errorMessage && <StatusCard $tone="error">{errorMessage}</StatusCard>}

      {canContinueAfterPayment && isAuthenticated && (
        <SecondaryButton type="button" onClick={handleNavigateToDashboard}>
          Ir para o painel
        </SecondaryButton>
      )}

      {canContinueAfterPayment && !isAuthenticated && (
        <PostPaymentActions>
          <SecondaryButton type="button" onClick={() => navigateToPostPaymentAuth(SIGN_UP_ROUTE)}>
            Criar conta para liberar acesso
          </SecondaryButton>
          <TertiaryButton type="button" onClick={() => navigateToPostPaymentAuth(SIGN_IN_ROUTE)}>
            Já tenho conta
          </TertiaryButton>
        </PostPaymentActions>
      )}
    </StyledForm>
  );
};

const StyledForm = styled.form`
  display: grid;
  gap: 1rem;
`;

const CopyBlock = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const FormTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #0f172a;
`;

const FormDescription = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.6;
`;

const GuestIdentityCard = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(15, 118, 110, 0.16);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.9), rgba(255, 255, 255, 0.96));
`;

const GuestIdentityTitle = styled.h4`
  margin: 0;
  font-size: 0.98rem;
  color: #0f172a;
`;

const GuestIdentityText = styled.p`
  margin: 0;
  color: #475569;
  line-height: 1.6;
  font-size: 0.94rem;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const FieldLabel = styled.label`
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
`;

const EmailInput = styled.input`
  min-height: 3.2rem;
  width: 100%;
  padding: 0 0.95rem;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  color: #0f172a;
  font-size: 0.98rem;

  &:focus {
    outline: none;
    border-color: rgba(15, 118, 110, 0.7);
    box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const FieldHint = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.88rem;
  line-height: 1.5;
`;

const ValidationText = styled.span`
  color: #b91c1c;
  font-size: 0.84rem;
  font-weight: 600;
`;

const SubmitButton = styled.button`
  min-height: 3.4rem;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #0f766e, #0ea5a4);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 140ms ease, opacity 140ms ease;
  box-shadow: 0 18px 30px rgba(15, 118, 110, 0.24);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const SecondaryButton = styled.button`
  min-height: 3rem;
  border-radius: 14px;
  border: 1px solid rgba(15, 118, 110, 0.24);
  background: #ffffff;
  color: #0f766e;
  font-weight: 700;
  cursor: pointer;
`;

const TertiaryButton = styled.button`
  min-height: 3rem;
  border-radius: 14px;
  border: 0;
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
  font-weight: 700;
  cursor: pointer;
`;

const PostPaymentActions = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const HelpText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const toneMap = {
  success: {
    background: "rgba(22, 163, 74, 0.12)",
    border: "rgba(22, 163, 74, 0.24)",
    color: "#166534",
  },
  info: {
    background: "rgba(14, 165, 233, 0.12)",
    border: "rgba(14, 165, 233, 0.22)",
    color: "#0f4c81",
  },
  error: {
    background: "rgba(220, 38, 38, 0.12)",
    border: "rgba(220, 38, 38, 0.22)",
    color: "#991b1b",
  },
};

const StatusCard = styled.div`
  padding: 0.95rem 1rem;
  border-radius: 16px;
  border: 1px solid ${({ $tone }) => toneMap[$tone].border};
  background: ${({ $tone }) => toneMap[$tone].background};
  color: ${({ $tone }) => toneMap[$tone].color};
  line-height: 1.5;
  font-weight: 600;
`;

export default StripeCheckoutForm;
